/** @jsx jsx */
import { Global, keyframes } from '@emotion/core';
import { Link } from 'gatsby';
import { Helmet } from 'react-helmet';
import { jsx } from 'theme-ui';

import {
  AIPanel,
  app,
  ConfidenceMeter,
  FeedScreen,
  Icon,
  Notification,
  Phone,
  PortfolioRows,
  TickerChip,
  Toggle,
} from '../components/newsimpact/mockups';

const SITE_URL = 'https://gokhana.dev';
const CONTACT_EMAIL = 'info@gokhana.dev';
const CTA_HREF = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
  'NewsImpact early access'
)}`;

const META = {
  title: 'NewsImpact — AI-powered context for BIST and market news',
  description:
    'NewsImpact filters KAP, macro, commodity and crypto news against your watchlist and uses AI to turn market impact into clear context. Early access for iOS.',
  image: `${SITE_URL}/newsimpact/og.png`,
};

// page palette: neutral ink surfaces with a cool indigo/teal accent.
// Green, coral and amber are reserved for the app's own sentiment/impact semantics.
const c = {
  bg: '#0A0B0D',
  surface: '#111317',
  surface2: '#16191E',
  border: 'rgba(255,255,255,0.08)',
  borderStrong: 'rgba(255,255,255,0.14)',
  text: '#EDEFF2',
  muted: '#A1A9B4',
  dim: '#7D8590',
  accent: '#A5B4FC',
  accentStrong: '#818CF8',
  teal: '#5EEAD4',
  gradient: 'linear-gradient(92deg, #C7D2FE 0%, #A5B4FC 40%, #99F6E4 100%)',
};

const font =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Inter, Roboto, "Helvetica Neue", Arial, sans-serif';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'NewsImpact',
  operatingSystem: 'iOS',
  applicationCategory: 'FinanceApplication',
  description: META.description,
  url: `${SITE_URL}/`,
  image: META.image,
  inLanguage: 'en',
  author: {
    '@type': 'Person',
    name: 'Gökhan Ayrancıoğlu',
    jobTitle: 'Lead Software Engineer',
    url: SITE_URL,
    sameAs: [
      'https://github.com/G-khan',
      'https://www.linkedin.com/in/gokhanadev/',
      'https://x.com/gokhanadev',
    ],
  },
};

const sources = ['KAP / BIST disclosures', 'Global finance RSS', 'Macro', 'Commodities', 'Crypto'];

const steps = [
  { title: 'Source', text: 'KAP, global RSS, macro, commodity and crypto feeds you choose.' },
  { title: 'Watchlist match', text: 'Each story is matched against your assets and keywords.' },
  { title: 'Critical alert', text: 'Only high-impact matches reach you, without duplicates.' },
  { title: 'AI impact analysis', text: 'Sentiment, bias, affected assets, outlook and risks.' },
  { title: 'You make the call', text: 'NewsImpact gives context. The decision stays yours.' },
];

const founderLinks = [
  { label: 'GitHub', href: 'https://github.com/G-khan' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/gokhanadev/' },
  { label: 'X', href: 'https://x.com/gokhanadev' },
];

/* ---------- layout primitives ---------- */

const Container = ({ children, ...props }) => (
  <div {...props} sx={{ maxWidth: 1160, mx: 'auto', px: ['16px', '24px', '32px'] }}>
    {children}
  </div>
);

const Eyebrow = ({ children }) => (
  <p
    sx={{
      m: 0,
      mb: '14px',
      fontSize: '13px',
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: c.accent,
    }}
  >
    {children}
  </p>
);

const H2 = ({ children, ...props }) => (
  <h2
    {...props}
    sx={{
      m: 0,
      mb: '16px',
      fontSize: ['30px', '40px', '46px'],
      lineHeight: 1.1,
      letterSpacing: '-0.03em',
      fontWeight: 700,
      color: c.text,
    }}
  >
    {children}
  </h2>
);

const Lead = ({ children, ...props }) => (
  <p
    {...props}
    sx={{
      m: 0,
      fontSize: ['17px', '19px'],
      lineHeight: 1.6,
      color: c.muted,
      maxWidth: 620,
    }}
  >
    {children}
  </p>
);

const PrimaryButton = ({ children = 'Join the early access list', compact = false }) => (
  <a
    href={CTA_HREF}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      minHeight: compact ? 40 : 52,
      px: compact ? '16px' : '24px',
      borderRadius: 999,
      bg: '#F5F7FA',
      color: '#0A0B0D',
      fontSize: compact ? '14px' : '16px',
      fontWeight: 600,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      boxShadow: '0 0 0 1px rgba(255,255,255,0.2), 0 10px 30px -10px rgba(165,180,252,0.55)',
      transition: 'transform .15s ease, box-shadow .15s ease',
      ':hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 0 0 1px rgba(255,255,255,0.3), 0 14px 36px -10px rgba(165,180,252,0.7)',
      },
      ':focus-visible': { outline: `2px solid ${c.accent}`, outlineOffset: 3 },
    }}
  >
    {children}
    {!compact && <Icon name="arrow" size={16} />}
  </a>
);

const GhostButton = ({ href, children }) => (
  <a
    href={href}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      minHeight: 52,
      px: '22px',
      borderRadius: 999,
      color: c.text,
      fontSize: '16px',
      fontWeight: 600,
      textDecoration: 'none',
      border: `1px solid ${c.borderStrong}`,
      bg: 'rgba(255,255,255,0.03)',
      transition: 'background-color .15s ease',
      ':hover': { bg: 'rgba(255,255,255,0.07)' },
      ':focus-visible': { outline: `2px solid ${c.accent}`, outlineOffset: 3 },
    }}
  >
    {children}
  </a>
);

const Card = ({ children, ...props }) => (
  <div
    {...props}
    sx={{
      position: 'relative',
      bg: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 24,
      p: ['20px', '28px'],
      overflow: 'hidden',
      backgroundImage:
        'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0) 40%)',
    }}
  >
    {children}
  </div>
);

const CardTitle = ({ children }) => (
  <h3
    sx={{
      m: 0,
      mb: '8px',
      fontSize: '20px',
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
      fontWeight: 650,
      color: c.text,
    }}
  >
    {children}
  </h3>
);

const CardText = ({ children }) => (
  <p sx={{ m: 0, fontSize: '15px', lineHeight: 1.6, color: c.muted }}>{children}</p>
);

/* ---------- page chrome ---------- */

const navLink = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 44,
  px: ['10px', '14px'],
  color: c.muted,
  fontSize: '15px',
  fontWeight: 500,
  textDecoration: 'none',
  borderRadius: 999,
  ':hover': { color: c.text },
  ':focus-visible': { outline: `2px solid ${c.accent}`, outlineOffset: 2 },
};

const Nav = () => (
  <header
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: 10,
      bg: 'rgba(10,11,13,0.72)',
      backdropFilter: 'saturate(160%) blur(14px)',
      borderBottom: `1px solid ${c.border}`,
    }}
  >
    <Container>
      <nav
        aria-label="Main"
        sx={{ display: 'flex', alignItems: 'center', height: 64, gap: ['2px', '6px'] }}
      >
        <Link
          to="/"
          aria-current="page"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            minHeight: 44,
            mr: 'auto',
            color: c.text,
            fontWeight: 700,
            fontSize: '17px',
            letterSpacing: '-0.01em',
            textDecoration: 'none',
          }}
        >
          <img
            src="/newsimpact/app-icon.webp"
            alt=""
            width="30"
            height="30"
            sx={{ borderRadius: 8, bg: '#fff' }}
          />
          NewsImpact
        </Link>
        <PrimaryButton compact>Get early access</PrimaryButton>
      </nav>
    </Container>
  </header>
);

const Footer = () => (
  <footer sx={{ borderTop: `1px solid ${c.border}`, py: ['36px', '48px'] }}>
    <Container>
      <p
        sx={{
          m: 0,
          mb: '28px',
          p: '16px 18px',
          borderRadius: 14,
          border: `1px solid ${c.border}`,
          bg: c.surface,
          fontSize: '14px',
          lineHeight: 1.6,
          color: c.muted,
        }}
      >
        <strong sx={{ color: c.text, fontWeight: 600 }}>Legal notice.</strong> NewsImpact does
        not provide investment advice, does not route orders and does not offer custody
        services. All content is for informational purposes only.
      </p>
      <div
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px 20px',
          fontSize: '14px',
          color: c.dim,
        }}
      >
        <span>© {new Date().getFullYear()} Gokhana · NewsImpact</span>
        <span sx={{ ml: [0, 'auto'], display: 'flex', flexWrap: 'wrap', gap: '4px 18px' }}>
          <a href={CTA_HREF} sx={{ color: c.muted, ':hover': { color: c.text } }}>
            {CONTACT_EMAIL}
          </a>
          <span>
            Powered by{' '}
            <Link to="/blogs/" sx={{ color: c.muted, ':hover': { color: c.text } }}>
              gokhana.dev
            </Link>
          </span>
        </span>
      </div>
    </Container>
  </footer>
);

const LandingSEO = () => (
  <Helmet>
    <html lang="en" />
    <title>{META.title}</title>
    <meta name="description" content={META.description} />
    <meta name="image" content={META.image} />
    <link rel="canonical" href={`${SITE_URL}/`} />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="gokhana.dev" />
    <meta property="og:title" content={META.title} />
    <meta property="og:description" content={META.description} />
    <meta property="og:url" content={`${SITE_URL}/`} />
    <meta property="og:image" content={META.image} />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />
    <meta property="og:image:alt" content="NewsImpact — AI-powered context for market news" />
    <meta property="og:locale" content="en_US" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" content="@gokhanadev" />
    <meta name="twitter:creator" content="@gokhanadev" />
    <meta name="twitter:title" content={META.title} />
    <meta name="twitter:description" content={META.description} />
    <meta name="twitter:image" content={META.image} />
    <meta name="twitter:image:alt" content="NewsImpact — AI-powered context for market news" />
    <meta name="theme-color" content={c.bg} />
    <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
  </Helmet>
);

/* ---------- sections ---------- */

const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const float = {
  animation: `${floatY} 6s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

const Hero = () => (
  <section
    aria-labelledby="hero-title"
    sx={{ position: 'relative', overflow: 'hidden', pt: ['48px', '72px', '88px'], pb: ['56px', '88px'] }}
  >
    {/* glow + grid backdrop */}
    <div
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background:
          'radial-gradient(60% 50% at 78% 18%, rgba(99,102,241,0.28) 0%, rgba(99,102,241,0) 70%), radial-gradient(45% 40% at 8% 90%, rgba(45,212,191,0.14) 0%, rgba(45,212,191,0) 70%)',
      }}
    />
    <div
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        maskImage: 'radial-gradient(70% 60% at 50% 30%, #000 0%, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(70% 60% at 50% 30%, #000 0%, transparent 75%)',
      }}
    />

    <Container sx={{ position: 'relative' }}>
      <div
        sx={{
          display: 'grid',
          gridTemplateColumns: ['1fr', '1fr', '1.08fr 0.92fr'],
          gap: ['56px', '64px', '40px'],
          alignItems: 'center',
        }}
      >
        <div>
          <p
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              m: 0,
              mb: '24px',
              px: '12px',
              py: '6px',
              borderRadius: 999,
              border: `1px solid ${c.borderStrong}`,
              bg: 'rgba(255,255,255,0.04)',
              fontSize: '13px',
              fontWeight: 500,
              color: c.muted,
            }}
          >
            <span
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bg: c.teal,
                boxShadow: `0 0 0 4px rgba(94,234,212,0.18)`,
              }}
            />
            iOS · Early access
          </p>
          <h1
            id="hero-title"
            sx={{
              m: 0,
              mb: '22px',
              fontSize: ['40px', '56px', '62px'],
              lineHeight: 1.04,
              letterSpacing: '-0.04em',
              fontWeight: 750,
              color: c.text,
            }}
          >
            Catch the news.{' '}
            <span
              sx={{
                background: c.gradient,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Understand the impact.
            </span>{' '}
            Weigh your next move faster.
          </h1>
          <Lead sx={{ mb: '32px' }}>
            NewsImpact filters KAP, macro, commodity and crypto news against your watchlist, and
            uses AI to turn market impact into clear context.
          </Lead>
          <div sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', mb: '40px' }}>
            <PrimaryButton />
            <GhostButton href="#how">How it works</GhostButton>
          </div>
          <div>
            <p sx={{ m: 0, mb: '10px', fontSize: '13px', color: c.dim }}>Sources</p>
            <ul sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {sources.map((s) => (
                <li
                  key={s}
                  sx={{
                    fontSize: '13px',
                    color: c.muted,
                    px: '11px',
                    py: '6px',
                    borderRadius: 999,
                    border: `1px solid ${c.border}`,
                    bg: 'rgba(255,255,255,0.02)',
                  }}
                >
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <figure sx={{ m: 0, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div sx={{ position: 'relative' }}>
            <div
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: '-40px',
                background:
                  'radial-gradient(50% 50% at 50% 50%, rgba(129,140,248,0.35) 0%, rgba(129,140,248,0) 70%)',
                filter: 'blur(10px)',
              }}
            />
            <Phone role="img" aria-label="NewsImpact watchlist feed with impact and sentiment labels">
              <FeedScreen />
            </Phone>
            <div
              sx={{
                display: ['none', 'block'],
                position: 'absolute',
                top: 210,
                left: -150,
                width: 250,
                ...float,
              }}
            >
              <Notification
                title="High impact · matches your watchlist"
                body="USDTRY · Inflation print comes in below consensus"
              />
            </div>
            <div
              sx={{
                display: ['none', 'block'],
                position: 'absolute',
                bottom: 70,
                right: -120,
                width: 210,
                p: '14px',
                borderRadius: 16,
                bg: 'rgba(26,29,32,0.92)',
                border: `1px solid ${app.border}`,
                backdropFilter: 'blur(12px)',
                boxShadow: '0 24px 48px -20px rgba(0,0,0,0.9)',
                ...float,
                animationDelay: '-3s',
              }}
            >
              <div sx={{ fontSize: '11px', color: app.muted, mb: '8px' }}>AI impact · BRENT</div>
              <ConfidenceMeter value={68} label="Medium confidence" />
            </div>
          </div>
          <figcaption sx={{ mt: '28px', fontSize: '12.5px', color: c.dim }}>
            Illustrative interface with sample data.
          </figcaption>
        </figure>
      </div>
    </Container>
  </section>
);

const Features = () => (
  <section id="features" aria-labelledby="features-title" sx={{ py: ['64px', '96px'], scrollMarginTop: 72 }}>
    <Container>
      <Eyebrow>Product</Eyebrow>
      <H2 id="features-title">The context you need, in one feed</H2>
      <Lead sx={{ mb: ['32px', '48px'] }}>
        NewsImpact separates the stories that matter to you from hundreds of headlines, and
        summarizes what each one could move, and how.
      </Lead>

      <div
        sx={{
          display: 'grid',
          gridTemplateColumns: ['1fr', '1fr 1fr', 'repeat(6, 1fr)'],
          gap: '16px',
        }}
      >
        {/* AI panel — large */}
        <Card sx={{ gridColumn: ['auto', '1 / -1', 'span 4'], gridRow: [null, null, 'span 2'] }}>
          <div
            sx={{
              display: 'grid',
              gridTemplateColumns: ['1fr', '1fr', '0.85fr 1.15fr'],
              gap: ['24px', '28px'],
              alignItems: 'start',
            }}
          >
            <div>
              <CardTitle>AI analysis panel</CardTitle>
              <CardText>
                Sentiment, directional bias, affected assets, short- and long-term outlook, key
                risks and a confidence score for every critical story.
              </CardText>
              <ul sx={{ listStyle: 'none', p: 0, m: 0, mt: '20px', display: 'grid', gap: '10px' }}>
                {['Structured, not a wall of text', 'Every output shows its confidence', 'Cached, so the same story is never analyzed twice'].map((t) => (
                  <li key={t} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '14.5px', color: c.muted }}>
                    <span sx={{ mt: '3px', color: c.teal }}>
                      <Icon name="check" size={14} />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <AIPanel />
          </div>
        </Card>

        <Card sx={{ gridColumn: ['auto', 'auto', 'span 2'] }}>
          <CardTitle>Multi-source</CardTitle>
          <CardText>KAP/BIST disclosures, global finance RSS, macro, commodities and crypto. Turn each source on or off.</CardText>
          <ul sx={{ listStyle: 'none', p: 0, m: 0, mt: '18px' }}>
            {[
              ['KAP / BIST', true],
              ['Global finance RSS', true],
              ['Macro', true],
              ['Commodities', false],
              ['Crypto', true],
            ].map(([label, on]) => (
              <li
                key={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: '8px',
                  borderTop: `1px solid ${c.border}`,
                  fontSize: '14px',
                  color: on ? c.text : c.dim,
                }}
              >
                {label}
                <span sx={{ ml: 'auto', display: 'inline-flex' }}>
                  <Toggle on={on} />
                </span>
              </li>
            ))}
          </ul>
        </Card>

        <Card sx={{ gridColumn: ['auto', 'auto', 'span 2'] }}>
          <CardTitle>Personal watchlist</CardTitle>
          <CardText>Stocks, indices, coins and keywords. Only the news that concerns you.</CardText>
          <div sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '18px' }}>
            {['THYAO', 'ASELS', 'XU100', 'USDTRY', 'BTC', 'XAUUSD'].map((t) => (
              <TickerChip key={t}>{t}</TickerChip>
            ))}
            <span
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                color: c.accent,
                bg: 'rgba(129,140,248,0.12)',
                px: '6px',
                py: '3px',
                borderRadius: 5,
              }}
            >
              “interest rate”
            </span>
          </div>
        </Card>

        <Card sx={{ gridColumn: ['auto', 'auto', 'span 3'] }}>
          <CardTitle>Smart alerts</CardTitle>
          <CardText>Critical stories that match your watchlist, without the noise or the duplicates.</CardText>
          <div sx={{ display: 'grid', gap: '8px', mt: '20px' }}>
            <Notification
              title="Critical · matches XU100"
              body="Policy statement released earlier than expected"
            />
            <Notification
              faded
              time="12m"
              title="High impact · matches BRENT"
              body="Brent crude extends gains after supply update"
            />
          </div>
        </Card>

        <Card sx={{ gridColumn: ['auto', 'auto', 'span 3'] }}>
          <CardTitle>Portfolio</CardTitle>
          <CardText>Cost basis, current value, P/L, stop and target levels, side by side with the news.</CardText>
          <div sx={{ mt: '20px', bg: app.card, border: `1px solid ${app.border}`, borderRadius: 14, p: '14px' }}>
            <PortfolioRows />
          </div>
        </Card>
      </div>
    </Container>
  </section>
);

const HowItWorks = () => (
  <section
    id="how"
    aria-labelledby="how-title"
    sx={{
      py: ['64px', '96px'],
      scrollMarginTop: 72,
      borderTop: `1px solid ${c.border}`,
      borderBottom: `1px solid ${c.border}`,
      background: 'linear-gradient(180deg, rgba(255,255,255,0.015), rgba(255,255,255,0))',
    }}
  >
    <Container>
      <Eyebrow>How it works</Eyebrow>
      <H2 id="how-title">From headline to context in seconds</H2>
      <ol
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          mt: ['32px', '48px'],
          display: 'grid',
          gridTemplateColumns: ['1fr', '1fr', 'repeat(5, 1fr)'],
          gap: ['0px', '0px', '16px'],
          position: 'relative',
        }}
      >
        {steps.map((s, i) => (
          <li
            key={s.title}
            sx={{
              position: 'relative',
              display: ['grid', 'grid', 'block'],
              gridTemplateColumns: '44px 1fr',
              gap: '16px',
              pb: ['28px', '28px', 0],
            }}
          >
            {/* connector */}
            {i < steps.length - 1 && (
              <span
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  bg: c.borderStrong,
                  left: [21, 21, 52],
                  top: [44, 44, 21],
                  width: [2, 2, 'calc(100% - 36px)'],
                  height: ['calc(100% - 44px)', 'calc(100% - 44px)', 2],
                  background: [
                    `linear-gradient(180deg, ${c.accentStrong}, rgba(129,140,248,0.1))`,
                    `linear-gradient(180deg, ${c.accentStrong}, rgba(129,140,248,0.1))`,
                    `linear-gradient(90deg, ${c.accentStrong}, rgba(129,140,248,0.1))`,
                  ],
                }}
              />
            )}
            <span
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: 14,
                fontWeight: 700,
                fontSize: '15px',
                color: i === steps.length - 1 ? '#0A0B0D' : c.text,
                bg: i === steps.length - 1 ? '#F5F7FA' : c.surface2,
                border: `1px solid ${c.borderStrong}`,
                mb: [0, 0, '18px'],
              }}
            >
              {i + 1}
            </span>
            <div>
              <h3 sx={{ m: 0, mb: '6px', fontSize: '17px', fontWeight: 650, color: c.text }}>{s.title}</h3>
              <p sx={{ m: 0, fontSize: '14.5px', lineHeight: 1.55, color: c.muted }}>{s.text}</p>
            </div>
          </li>
        ))}
      </ol>
    </Container>
  </section>
);

const Trust = () => (
  <section aria-labelledby="trust-title" sx={{ py: ['64px', '96px'] }}>
    <Container>
      <div
        sx={{
          display: 'grid',
          gridTemplateColumns: ['1fr', '1fr', '1fr 1fr'],
          gap: ['36px', '48px', '64px'],
          alignItems: 'center',
        }}
      >
        <div>
          <Eyebrow>Transparency</Eyebrow>
          <H2 id="trust-title">Context you can verify</H2>
          <Lead sx={{ mb: '28px' }}>
            AI output is a summary of context, not a verdict. NewsImpact always shows where a story
            came from and how sure the analysis is.
          </Lead>
          <ul sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: '14px' }}>
            {[
              ['clock', 'Source, publish time and analysis time are always visible.'],
              ['alert', 'Low-confidence analyses are clearly flagged.'],
              ['check', 'You stay in control: no orders, no custody, no advice.'],
            ].map(([icon, text]) => (
              <li key={text} sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start', fontSize: '16px', color: c.text }}>
                <span
                  sx={{
                    flex: '0 0 auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    bg: c.surface2,
                    border: `1px solid ${c.border}`,
                    color: c.accent,
                  }}
                >
                  <Icon name={icon} size={15} />
                </span>
                <span sx={{ pt: '5px' }}>{text}</span>
              </li>
            ))}
          </ul>
          <div
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              mt: '32px',
              p: '12px 16px',
              borderRadius: 14,
              border: `1px solid ${c.borderStrong}`,
              bg: 'linear-gradient(135deg, rgba(129,140,248,0.14), rgba(94,234,212,0.06))',
            }}
          >
            <span sx={{ fontSize: '13px', color: c.muted }}>AI layer</span>
            <strong sx={{ fontSize: '17px', color: c.text, letterSpacing: '-0.01em' }}>Powered by Claude</strong>
            <span sx={{ fontSize: '13px', color: c.dim }}>Anthropic</span>
          </div>
        </div>

        <Card>
          <div sx={{ display: 'flex', alignItems: 'center', mb: '16px' }}>
            <span sx={{ fontSize: '14px', fontWeight: 600, color: c.text }}>Analysis details</span>
            <span
              sx={{
                ml: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                fontWeight: 600,
                color: app.high,
                bg: 'rgba(255,185,104,0.12)',
                px: '10px',
                py: '5px',
                borderRadius: 999,
              }}
            >
              <Icon name="alert" size={12} color={app.high} /> Low confidence
            </span>
          </div>
          <dl
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '0',
              fontSize: '14px',
              'dt, dd': { py: '11px', borderTop: `1px solid ${c.border}` },
              dt: { color: c.dim, pr: '16px' },
              dd: { m: 0, color: c.text, textAlign: 'right' },
            }}
          >
            <dt>Source</dt>
            <dd>Global finance RSS</dd>
            <dt>Published</dt>
            <dd>09:32</dd>
            <dt>Analyzed</dt>
            <dd>09:33</dd>
            <dt>Matched</dt>
            <dd>
              <TickerChip>XU100</TickerChip>
            </dd>
          </dl>
          <div sx={{ mt: '16px', p: '14px', borderRadius: 14, bg: app.card, border: `1px solid ${app.border}` }}>
            <ConfidenceMeter
              value={34}
              color={app.high}
              label="Limited information in the source. Treat this analysis with caution."
            />
          </div>
        </Card>
      </div>
    </Container>
  </section>
);

const Founder = () => (
  <section aria-labelledby="founder-title" sx={{ pb: ['64px', '96px'] }}>
    <Container>
      <div
        sx={{
          display: 'grid',
          gridTemplateColumns: ['1fr', '1fr', '1fr 1fr'],
          gap: '16px',
        }}
      >
        <Card>
          <Eyebrow>Founder</Eyebrow>
          <div sx={{ display: 'flex', alignItems: 'center', gap: '16px', mb: '16px' }}>
            <img
              src="/newsimpact/founder.webp"
              alt="Gökhan Ayrancıoğlu"
              width="64"
              height="64"
              loading="lazy"
              decoding="async"
              sx={{ borderRadius: '50%', border: `1px solid ${c.borderStrong}` }}
            />
            <div>
              <h2 id="founder-title" sx={{ m: 0, fontSize: '20px', fontWeight: 650, color: c.text }}>
                Gökhan Ayrancıoğlu
              </h2>
              <p sx={{ m: 0, fontSize: '15px', color: c.muted }}>Lead Software Engineer</p>
            </div>
          </div>
          <CardText>
            Backend engineer building NewsImpact to shorten the time between a headline and an
            informed view of what it means.
          </CardText>
          <ul sx={{ listStyle: 'none', p: 0, m: 0, mt: '20px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {founderLinks.map((l) => (
              <li key={l.label}>
                <a
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ ...navLink, px: '14px', border: `1px solid ${c.border}`, color: c.text }}
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <Link to="/blogs/" sx={{ ...navLink, px: '14px', border: `1px solid ${c.border}`, color: c.text }}>
                Blog
              </Link>
            </li>
          </ul>
        </Card>

        <div
          sx={{
            position: 'relative',
            borderRadius: 24,
            p: '1px',
            background: 'linear-gradient(135deg, rgba(165,180,252,0.7), rgba(94,234,212,0.35) 50%, rgba(255,255,255,0.08))',
          }}
        >
          <div
            sx={{
              height: '100%',
              borderRadius: 23,
              p: ['24px', '32px'],
              bg: c.surface,
              backgroundImage:
                'radial-gradient(80% 80% at 100% 0%, rgba(99,102,241,0.22) 0%, rgba(99,102,241,0) 60%)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <h2 sx={{ m: 0, mb: '12px', fontSize: ['26px', '32px'], lineHeight: 1.15, letterSpacing: '-0.025em', color: c.text }}>
              Be among the first to try NewsImpact
            </h2>
            <p sx={{ m: 0, mb: '24px', fontSize: '16px', lineHeight: 1.6, color: c.muted }}>
              Drop us a line and we will let you know when early access opens:{' '}
              <a href={CTA_HREF} sx={{ color: c.accent }}>
                {CONTACT_EMAIL}
              </a>
            </p>
            <div>
              <PrimaryButton />
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

export default () => (
  <div sx={{ bg: c.bg, color: c.text, fontFamily: font, minHeight: '100vh' }}>
    <Global
      styles={{
        '*, *::before, *::after': { boxSizing: 'border-box' },
        'html, body': { margin: 0, background: c.bg },
        body: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
        'html': { scrollBehavior: 'smooth' },
        '@media (prefers-reduced-motion: reduce)': { html: { scrollBehavior: 'auto' } },
      }}
    />
    <LandingSEO />
    <Nav />
    <main>
      <Hero />
      <Features />
      <HowItWorks />
      <Trust />
      <Founder />
    </main>
    <Footer />
  </div>
);
